import React from 'react'
import { MessengerButton } from 'component-library/buttons/MessengerButton/MessengerButton'
import {
    StyledWrapper,
    StyledBlock,
    StyledList,
    StyledBlockHeader,
    StyledLinkBlock,
    StyledLink,
    StyledTitle,
    StyledText,
} from 'component-library/navigation/TheWestFooterNetworkLinks/TheWestFooterNetworkLinks.styled'
import { SocialLinks } from 'component-library/navigation/SocialLinks/SocialLinks'
import {
    theWestSectionDefinitions,
    TheWestSection,
} from 'component-library/__styling/settings/sections/thewest'
import { useTheme } from '@emotion/react'

interface Link {
    name: string
    link: string
}

interface NetworkLink {
    name: string
    links: Link[]
}

type RegionalSections = Exclude<TheWestSection, 'default'>

interface BaseSocial {
    facebookMessenger: string
    facebook: string
    email: string
}

interface Address {
    street: string
    city: string
    state: 'WA'
    postcode: number
    phone: string
}

// Regional Social links just have email, facebook & messenger, hence the BaseSocial type
// Regionals also include the Address interface
type SectionSocialLinks = {
    [section in RegionalSections]: BaseSocial & Address
}

export type SectionedSocialLinks = SectionSocialLinks & {
    default: BaseSocial & {
        twitter: string
        instagram: string
        rss: string
    }
}

export interface Props {
    networkLinks: {
        regional: NetworkLink[]
        thewest: NetworkLink[]
    }
    socialLinks: SectionedSocialLinks
}

export const TheWestFooterNetworkLinks: React.FC<Props> = ({ networkLinks, socialLinks }) => {
    const { section } = useTheme()

    const isRegionalSite = section !== 'default'
    const linksToDisplay = isRegionalSite ? networkLinks.regional : networkLinks.thewest

    const renderSocialLinks = () => {
        if (isRegionalSite) {
            const sectionedSocialLinks = socialLinks[section as TheWestSection] as (BaseSocial &
                Address)

            const { street, city, state, postcode, phone } = sectionedSocialLinks
            const { title } = theWestSectionDefinitions[section as TheWestSection]

            return (
                <StyledBlock>
                    <StyledBlockHeader>Contact Us</StyledBlockHeader>
                    <StyledTitle>{title}</StyledTitle>
                    <StyledText>{street}</StyledText>
                    <StyledText>{`${city} ${state} ${postcode}`}</StyledText>
                    <StyledText>{`Tel ${phone}`}</StyledText>
                    <MessengerButton link={sectionedSocialLinks.facebookMessenger} />
                    <SocialLinks {...sectionedSocialLinks} />
                </StyledBlock>
            )
        }

        return (
            <StyledBlock>
                <StyledBlockHeader>Connect with us</StyledBlockHeader>
                <MessengerButton link={socialLinks.default.facebookMessenger} />
                <SocialLinks {...socialLinks.default} />
            </StyledBlock>
        )
    }

    return (
        <StyledWrapper>
            {linksToDisplay.map(item => {
                return (
                    <StyledBlock key={item.name}>
                        <StyledBlockHeader>{item.name}</StyledBlockHeader>
                        <StyledList>
                            {item.links.map(({ name, link }) => {
                                return (
                                    <StyledLinkBlock key={name}>
                                        <StyledLink to={link}>{name}</StyledLink>
                                    </StyledLinkBlock>
                                )
                            })}
                        </StyledList>
                    </StyledBlock>
                )
            })}
            {renderSocialLinks()}
        </StyledWrapper>
    )
}
